import { motion } from "framer-motion";
import { useState } from "react";
import Images from "../../utils/Images";

export default function NewsletterForm() {
  const [copied, setCopied] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [inputType, setInputType] = useState("text");
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState("");
  const ussdCode = "*380*300#";

  const handleUSSDClick = () => {
    // Open dialer with the USSD code
    window.location.href = `tel:${ussdCode}`;
  };

  const handleCopyClick = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(ussdCode).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = ussdCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digit characters for validation
    const cleanPhone = phone.replace(/\D/g, "");
    // Accept various phone number formats (7-15 digits)
    return cleanPhone.length >= 7 && cleanPhone.length <= 15;
  };

  const detectInputType = (value) => {
    // If it contains @, treat as email
    if (value.includes("@")) {
      return "email";
    }
    // If it contains only digits, spaces, dashes, parentheses, or +, treat as phone
    if (
      /^[\d\s\-\(\)\+]+$/.test(value) &&
      value.replace(/\D/g, "").length >= 3
    ) {
      return "tel";
    }
    // Default to text for mixed content
    return "text";
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value === "") {
      setIsValid(true);
      setValidationMessage("");
      setInputType("text");
      return;
    }

    const detectedType = detectInputType(value);
    setInputType(detectedType);

    let valid = false;
    let message = "";

    if (detectedType === "email") {
      valid = validateEmail(value);
      message = valid ? "" : "Please enter a valid email address";
    } else if (detectedType === "tel") {
      valid = validatePhone(value);
      message = valid ? "" : "Please enter a valid phone number";
    } else {
      // For mixed or unclear input, be more lenient
      valid = value.length >= 3;
      message = valid ? "" : "Please enter at least 3 characters";
    }

    setIsValid(valid);
    setValidationMessage(message);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!inputValue.trim()) {
      setIsValid(false);
      setValidationMessage("Please enter your email or phone number");
      return;
    }

    if (!isValid) {
      return;
    }

    // Here you would typically send the data to your backend
    console.log(`Submitting ${inputType}:`, inputValue);

    // Reset form after successful submission
    setInputValue("");
    setIsValid(true);
    setValidationMessage("");
    setInputType("text");

    // You could add a success message here
    alert(`Thank you! We'll contact you at ${inputValue}`);
  };
  return (
    <section className="relative py-16 mt-16 bg-black sm:mt-24 lg:mt-32 sm:py-20">
      {/* Dotted background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:16px_16px]" />

      {/* Decorative arcs */}
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full border-8 pointer-events-none border-emerald-600/40" />
      <div className="absolute bottom-10 -right-20 w-64 h-64 rounded-full border-8 pointer-events-none border-emerald-600/30" />

      <div className="grid relative grid-cols-1 gap-10 px-4 mx-auto max-w-7xl sm:px-6 lg:grid-cols-3 lg:items-center lg:gap-12 lg:px-8">
        {/* Copy */}
        <div className="col-span-2 max-w-2xl">
          <motion.h2
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-3 text-3xl font-extrabold leading-tight text-white sm:text-4xl lg:text-5xl"
          >
            Insure and secure your trip
            <div className="relative inline-block">
              <motion.div
                onClick={handleUSSDClick}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group relative inline-block text-[#22C55E] hover:text-emerald-400 transition-all duration-300 cursor-pointer select-none"
                title="Tap to dial or click copy icon to copy"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleUSSDClick();
                  }
                }}
              >
                <span className="relative z-10 flex items-center gap-2">
                  {ussdCode}
                  {/* Phone icon to indicate it's clickable */}
                  <svg className="w-5 h-5 opacity-60 group-hover:opacity-100 transition-opacity duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </span>
                
                {/* Hover effect background */}
                <div className="absolute inset-0 bg-emerald-600/10 rounded-lg scale-0 group-hover:scale-100 transition-transform duration-300 origin-center" />
                
                {/* Pulsing ring effect on mobile */}
                <div className="absolute inset-0 rounded-lg ring-2 ring-emerald-400/30 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                
                {/* Copy button */}
                <button
                  onClick={handleCopyClick}
                  className="absolute -right-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-all duration-300 p-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 rounded-full backdrop-blur-sm hover:scale-110"
                  title="Copy USSD code"
                >
                  {copied ? (
                    <svg className="w-4 h-4 text-emerald-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  )}
                </button>
              </motion.div>
              
              {/* Copy success message */}
              {copied && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.8 }}
                  className="absolute -bottom-8 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap"
                >
                  Copied!
                </motion.div>
              )}
            </div>
            <span className="block text-[#22C55E]">
              and choose your cover in seconds
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="mb-8 max-w-lg text-sm leading-relaxed text-gray-300 sm:text-base lg:max-w-lg"
          >
            Stay updated with essential travel info, exclusive insurance deals,
            and the latest policy changes delivered straight to you.
          </motion.p>

          {/* Form */}
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            onSubmit={handleSubmit}
            className="w-full max-w-xl"
          >
            <div className="flex gap-3 items-center p-2 rounded-xl ring-1 backdrop-blur bg-white/5 ring-white/10">
              <input
                type={inputType}
                value={inputValue}
                onChange={handleInputChange}
                placeholder="Enter your email address or phone number"
                className={`flex-1 px-4 w-full h-12 text-white bg-transparent rounded-lg placeholder:text-gray-400 focus:outline-none transition-colors ${
                  !isValid && inputValue ? "border-2 border-red-500" : ""
                }`}
              />
              <button
                type="submit"
                className="inline-flex justify-center items-center px-6 h-12 font-semibold text-black bg-emerald-600 rounded-lg transition shrink-0 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={!isValid || !inputValue.trim()}
              >
                Submit
              </button>
            </div>

            {/* Validation message */}
            {validationMessage && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 text-sm text-red-400 text-left"
              >
                {validationMessage}
              </motion.p>
            )}

            {/* Input type indicator */}
            {inputValue && isValid && (
              <motion.p
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-1 text-xs text-gray-400 text-left"
              >
                {inputType === "email"
                  ? "📧 Email detected"
                  : inputType === "tel"
                  ? "📱 Phone number detected"
                  : "✉️ Enter email or phone number"}
              </motion.p>
            )}
          </motion.form>
        </div>

        {/* Phone visual */}
        <motion.div
          initial={{ opacity: 0, y: 20, rotate: 8 }}
          whileInView={{ opacity: 1, y: 0, rotate: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="relative z-10 mx-auto w-full max-w-md hidden lg:block lg:absolute lg:-right-6 lg:-top-12 lg:max-w-2xl xl:-right-8 xl:-top-[85px] xl:max-w-3xl 2xl:-top-28 2xl:max-w-4xl"
        >
          <img
            src={Images.phone_mock_up_one}
            alt="Phone CTA"
            className="mx-auto w-[95%] rotate-0 transform-gpu origin-bottom scale-y-[1.08] drop-shadow-[0_25px_40px_rgba(34,197,94,0.25)] sm:w-[92%] lg:w-[95%] xl:w-[90%] 2xl:w-[85%]"
          />
        </motion.div>
      </div>
    </section>
  );
}
